import { neonConfig } from '@neondatabase/serverless';

// Configure WebSocket for serverless environments (optional but recommended)
if (typeof window !== 'undefined') {
  neonConfig.fetchConnectionCache = true;
}

import axios from 'axios';

let dataApiUrl = import.meta.env.VITE_NEON_DATA_API_URL || '';
if (dataApiUrl.startsWith('ttps://')) {
  dataApiUrl = 'h' + dataApiUrl;
} else if (dataApiUrl && !dataApiUrl.startsWith('http')) {
  dataApiUrl = 'https://' + dataApiUrl;
}

class PostgrestBuilder {
  constructor(baseUrl, tableName, headers) {
    this.baseUrl = baseUrl;
    this.tableName = tableName;
    this.headers = headers;
    this.params = new URLSearchParams();
  }

  select(columns = '*') {
    if (columns !== '*') this.params.append('select', columns);
    return this;
  }

  eq(column, value) {
    this.params.append(column, `eq.${value}`);
    return this;
  }

  neq(column, value) {
    this.params.append(column, `neq.${value}`);
    return this;
  }

  gte(column, value) {
    this.params.append(column, `gte.${value}`);
    return this;
  }

  lte(column, value) {
    this.params.append(column, `lte.${value}`);
    return this;
  }

  ilike(column, pattern) {
    this.params.append(column, `ilike.${pattern}`);
    return this;
  }

  is(column, value) {
    this.params.append(column, `is.${value}`);
    return this;
  }

  in(column, values) {
    this.params.append(column, `in.(${values.join(',')})`);
    return this;
  }

  order(column, { ascending = true } = {}) {
    this.params.append('order', `${column}.${ascending ? 'asc' : 'desc'}`);
    return this;
  }

  single() {
    this.headers['Accept'] = 'application/vnd.pgrst.object+json'; 
    return this;
  }

  limit(count) {
    this.params.append('limit', count);
    return this;
  }

  async buildRequest(method, data = null) {
      let queryStr = '';
      let queryParams = [];

      try {
        if (method === 'GET') {
          const select = this.params.get('select') || '*';
          let selectSql = '*';
          if (select === 'id') selectSql = 'id';
          
          if (this.tableName === 'lasea_inventory') {
             if (select.includes('client:lasea_users')) {
               queryStr = `
                 SELECT i.*,
                 (
                   SELECT row_to_json(u.*)
                   FROM lasea_users u
                   WHERE u.id = i.client_id
                 ) as client,
                 COALESCE(
                   (
                     SELECT json_agg(p.* ORDER BY display_order ASC) 
                     FROM lasea_inventory_photos p 
                     WHERE p.inventory_id = i.id
                   ),
                   '[]'::json
                 ) as photos
                 FROM lasea_inventory i
               `;
             } else if (select.includes('photos:')) {
               queryStr = `
                 SELECT i.*,
                 COALESCE(
                   (
                     SELECT json_agg(p.* ORDER BY display_order ASC) 
                     FROM lasea_inventory_photos p 
                     WHERE p.inventory_id = i.id
                   ),
                   '[]'::json
                 ) as photos
                 FROM lasea_inventory i
               `;
             } else {
               queryStr = `SELECT ${selectSql} FROM ${this.tableName}`;
             }
          } else {
             queryStr = `SELECT ${selectSql} FROM ${this.tableName}`;
          }

          const whereClauses = [];
          const useAlias = this.tableName === 'lasea_inventory' && (select.includes('photos:') || select.includes('client:'));
          for (const [k, v] of this.params.entries()) {
            if (k === 'select' || k === 'order' || k === 'limit') continue;
            const col = useAlias ? `i."${k}"` : `"${k}"`;
            if (v.startsWith('eq.')) {
              let val = v.substring(3);
              if (val === 'true') val = true;
              else if (val === 'false') val = false;
              else if (val === 'null') val = null;
              if (val === null) {
                whereClauses.push(`${col} IS NULL`);
              } else {
                queryParams.push(val);
                whereClauses.push(`${col} = $${queryParams.length}`);
              }
            } else if (v.startsWith('neq.')) {
              let val = v.substring(4);
              queryParams.push(val);
              whereClauses.push(`${col} != $${queryParams.length}`);
            } else if (v.startsWith('gte.')) {
              queryParams.push(v.substring(4));
              whereClauses.push(`${col} >= $${queryParams.length}`);
            } else if (v.startsWith('lte.')) {
              queryParams.push(v.substring(4));
              whereClauses.push(`${col} <= $${queryParams.length}`);
            } else if (v.startsWith('ilike.')) {
              queryParams.push(v.substring(6));
              whereClauses.push(`${col} ILIKE $${queryParams.length}`);
            } else if (v.startsWith('is.')) {
              const val = v.substring(3);
              if (val === 'null') whereClauses.push(`${col} IS NULL`);
              else if (val === 'true') whereClauses.push(`${col} IS TRUE`);
              else if (val === 'false') whereClauses.push(`${col} IS FALSE`);
            } else if (v.startsWith('in.(')) {
              const vals = v.slice(4, -1).split(',').map(s => s.trim());
              const placeholders = vals.map(val => { queryParams.push(val); return `$${queryParams.length}`; });
              whereClauses.push(`${col} IN (${placeholders.join(', ')})`);
            }
          }

          if (whereClauses.length > 0) {
            queryStr += ' WHERE ' + whereClauses.join(' AND ');
          }

          const order = this.params.get('order');
          if (order) {
            const [col, dir] = order.split('.');
            if (this.tableName === 'lasea_inventory' && select.includes('photos:')) {
                queryStr += ` ORDER BY i."${col}" ${dir === 'desc' ? 'DESC' : 'ASC'}`;
            } else {
                queryStr += ` ORDER BY "${col}" ${dir === 'desc' ? 'DESC' : 'ASC'}`;
            }
          }

          const limitValue = this.params.get('limit');
          if (limitValue) queryStr += ` LIMIT ${parseInt(limitValue)}`;

        } 
        else if (method === 'POST') {
           if (!Array.isArray(data)) data = [data]; 
           const cols = Object.keys(data[0]);
           const placeholders = data.map((_, i) => 
               `(${cols.map((_, j) => `$${i * cols.length + j + 1}`).join(', ')})`
           ).join(', ');
           const vals = data.flatMap(d => cols.map(c => d[c]));
           queryStr = `INSERT INTO ${this.tableName} (${cols.map(c => `"${c}"`).join(', ')}) VALUES ${placeholders} RETURNING *`;
           queryParams = vals;
        } 
        else if (method === 'PATCH') {
           const cols = Object.keys(data);
           const setClauses = cols.map((c, i) => `"${c}" = $${i + 1}`);
           queryParams = Object.values(data);
           
           const patchWhere = [];
           for (const [k, v] of this.params.entries()) {
             if (k === 'select' || k === 'order' || k === 'limit') continue;
             if (v.startsWith('eq.')) {
                let val = v.substring(3);
                if (val === 'true') val = true;
                else if (val === 'false') val = false;
                queryParams.push(val);
                patchWhere.push(`"${k}" = $${queryParams.length}`);
             }
           }
           queryStr = `UPDATE ${this.tableName} SET ${setClauses.join(', ')}`;
           if (patchWhere.length > 0) queryStr += ' WHERE ' + patchWhere.join(' AND ');
           queryStr += ' RETURNING *';
        }
        else if (method === 'DELETE') {
           const delWhere = [];
           for (const [k, v] of this.params.entries()) {
             if (k === 'select' || k === 'order' || k === 'limit') continue;
             if (v.startsWith('eq.')) {
                let val = v.substring(3);
                queryParams.push(val);
                delWhere.push(`"${k}" = $${queryParams.length}`);
             }
           }
           queryStr = `DELETE FROM ${this.tableName}`;
           if (delWhere.length > 0) queryStr += ' WHERE ' + delWhere.join(' AND ');
           queryStr += ' RETURNING *';
        }

        let WORKER_URL = import.meta.env.VITE_WORKER_URL;
        let baseUrl = WORKER_URL.startsWith('ttps://') ? 'h' + WORKER_URL : WORKER_URL;
        if (baseUrl && !baseUrl.startsWith('http')) baseUrl = 'https://' + baseUrl;

        const response = await axios({
           method: 'POST',
           url: `${baseUrl}/data/query`,
           data: { query: queryStr, params: queryParams },
           headers: { 'Content-Type': 'application/json', 'X-Worker-Auth': import.meta.env.VITE_WORKER_SECRET }
        });
        
        let resultData = response.data;
        
        // Handle @neondatabase/serverless returning { rows: [...] } instead of direct array
        let isRowsObject = resultData && typeof resultData === 'object' && !Array.isArray(resultData) && Array.isArray(resultData.rows);
        let rowsArray = isRowsObject ? resultData.rows : resultData;

        if (method === 'GET' && this.headers['Accept'] === 'application/vnd.pgrst.object+json') {
             // single() unwrap
             resultData = Array.isArray(rowsArray) ? (rowsArray[0] || null) : rowsArray;
        } else {
             resultData = rowsArray;
        }

        return { data: resultData, error: null };
      } catch (error) {
        const errorData = error.response?.data;
        const errMsg = errorData?.message || errorData?.error || error.message;
        console.error('Neon API Error Detail:', errMsg, 'Query:', errorData?.query, 'Params:', errorData?.params);
        return { data: null, error: errMsg };
      }
  }

  then(onSuccess, onError) {
    const method = this._method || 'GET';
    const data = this._data || null;
    return this.buildRequest(method, data).then(onSuccess, onError);
  }

  insert(data) {
    this.headers['Prefer'] = 'return=representation';
    this._method = 'POST';
    this._data = data;
    return this;
  }

  update(data) {
    this.headers['Prefer'] = 'return=representation';
    this._method = 'PATCH';
    this._data = data;
    return this;
  }

  delete() {
    this._method = 'DELETE';
    return this;
  }
}

export const neonClient = {
  from: (tableName) => {
    const workerSecret = import.meta.env.VITE_WORKER_SECRET;
    const token = localStorage.getItem('lasea-auth-token');
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-Worker-Auth': workerSecret,
    };
    // token is bypassed by worker, but Neon Data API originally used it.
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    // We pass workerUrl to PostgrestBuilder so it can query the worker proxy
    return new PostgrestBuilder('', tableName, headers);
  }
};

/**
 * Helper to get auth headers from local storage/session
 */
const getAuthHeaders = () => {
  // Assuming a token is stored in local storage after a login
  const token = localStorage.getItem('neon-auth-token'); 
  const headers = {
    'Accept': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

const handleResponse = async (response) => {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.message || `HTTP Error ${response.status}`);
  }
  return { data, error: null };
};
